import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wallet } from "lucide-react";
import SEO from "@/components/SEO";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMessage(data.message || "If that email is registered, we've sent a password-reset link.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center bg-brand-bg font-body">
      <SEO
        title="Forgot your password"
        description="Reset your Tracker Daily Money password. We'll email you a secure reset link so you can regain access to your ledger."
        path="/forgot-password"
        noindex
      />
      <div className="max-w-md w-full mx-auto p-8">
        <Link to="/login" className="flex items-center gap-2 w-fit mb-10" data-testid="brand-logo">
          <div className="h-9 w-9 rounded-md bg-brand-primary flex items-center justify-center">
            <Wallet className="h-4 w-4 text-brand-bg" strokeWidth={1.75} />
          </div>
          <span className="font-display font-black text-xl tracking-tight text-brand-charcoal">Tracker Daily</span>
        </Link>

        <div className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-brand-muted">Password recovery</div>
        <h1 className="font-display font-black text-4xl tracking-tight text-brand-charcoal mb-3">Forgot your password?</h1>
        <p className="text-brand-muted mb-8">Enter the email on your account and we&apos;ll send you a link to reset your password.</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email" className="text-brand-charcoal">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com" data-testid="forgot-email"
              className="mt-1 border-brand-border bg-brand-surface" />
          </div>
          {error && <div data-testid="forgot-error" className="text-sm text-brand-danger bg-brand-danger/10 border border-brand-danger/20 rounded-md px-3 py-2">{error}</div>}
          <Button type="submit" disabled={loading} data-testid="forgot-submit"
            className="w-full bg-brand-primary text-brand-bg hover:bg-brand-primaryHover rounded-md py-6 text-base font-semibold">
            {loading ? "Sending…" : "Send reset link"}
          </Button>
        </form>

        {message && (
          <div className="mt-6 rounded-md border border-brand-border bg-brand-surface p-4" data-testid="forgot-message">
            <div className="text-sm text-brand-charcoal">{message}</div>
          </div>
        )}

        <p className="mt-6 text-sm text-brand-muted">
          Remembered it?{" "}
          <Link to="/login" className="text-brand-primary font-semibold hover:underline" data-testid="link-login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
